import { Box, Dialog, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LuDoorOpen, LuAlertTriangle } from 'react-icons/lu';

interface ConfirmCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  guestName: string | null;
  propertyName: string | null;
  isConfirming: boolean;
}

const GuestAvatar = ({ name }: { name: string | null }) => {
  const initial = name ? name.charAt(0).toUpperCase() : '?';
  const colors = ['#5B3FFF', '#E91E63', '#FF9800', '#4CAF50', '#2196F3', '#9C27B0'];
  const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <Box
      sx={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: colors[colorIndex],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '10px', fontWeight: 700, color: '#FFFFFF', lineHeight: 1 }}>
        {initial}
      </Typography>
    </Box>
  );
};

const ConfirmCheckoutModal = ({ open, onClose, onConfirm, guestName, propertyName, isConfirming }: ConfirmCheckoutModalProps) => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '400px',
          maxWidth: '95vw',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0px 20px 138.6px 0px rgba(0,0,0,0.25)',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', p: '24px' }}>

        {/* Icon + Title */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '24px',
              backgroundColor: 'rgba(91, 63, 255, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LuDoorOpen size={24} color="#5B3FFF" />
          </Box>
          <Typography
            sx={{
              fontFamily: 'Tajawal, sans-serif',
              fontWeight: 700,
              fontSize: '18px',
              color: '#1A1A2E',
              lineHeight: '27px',
              textAlign: 'center',
              width: '100%',
            }}
          >
            {isAr ? 'تأكيد خروج الضيف' : 'Confirm Guest Checkout'}
          </Typography>
        </Box>

        {/* Property Info */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Guest + Property row */}
          <Box
            sx={{
              backgroundColor: '#F9FAFB',
              borderRadius: '8px',
              px: '16px',
              py: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Property name — start side */}
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '13px', color: '#6B7280', lineHeight: '19.5px' }}>
              {propertyName || '—'}
            </Typography>
            {/* Guest name + avatar — end side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '14px', color: '#435363', lineHeight: '21px' }}>
                {guestName || '—'}
              </Typography>
              <GuestAvatar name={guestName} />
            </Box>
          </Box>

          {/* Warning box */}
          <Box
            sx={{
              backgroundColor: '#FFFBEB',
              border: '1px solid #FCD34D',
              borderRadius: '8px',
              px: '13px',
              py: '9px',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Tajawal, sans-serif',
                fontWeight: 500,
                fontSize: '13px',
                color: '#92400E',
                lineHeight: '19.5px',
                textAlign: 'start',
                flex: 1,
              }}
            >
              {isAr
                ? 'سيتم تحديث حالة العقار إلى متاح وإرسال إشعار لفريق التنظيف'
                : 'Property status will be updated to available and a notification will be sent to the cleaning team'}
            </Typography>
            <LuAlertTriangle size={16} color="#F59E0B" style={{ flexShrink: 0, marginTop: '2px' }} />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: '12px' }}>
          {/* Cancel */}
          <Box
            onClick={onClose}
            sx={{
              flex: 1,
              height: '41px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              backgroundColor: '#FFFFFF',
              '&:hover': { backgroundColor: '#F9FAFB' },
            }}
          >
            <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontSize: '12px', color: '#6B7280' }}>
              {isAr ? 'تراجع' : 'Cancel'}
            </Typography>
          </Box>

          {/* Confirm */}
          <Box
            onClick={!isConfirming ? onConfirm : undefined}
            sx={{
              flex: 1,
              height: '41px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              cursor: isConfirming ? 'not-allowed' : 'pointer',
              backgroundColor: isConfirming ? '#A78BFF' : '#5B3FFF',
              '&:hover': !isConfirming ? { backgroundColor: '#4A32E0' } : {},
            }}
          >
            {isConfirming ? (
              <Box
                sx={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#FFFFFF',
                  animation: 'spin 0.7s linear infinite',
                  '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                }}
              />
            ) : (
              <Typography sx={{ fontFamily: 'Tajawal, sans-serif', fontWeight: 700, fontSize: '12px', color: '#FFFFFF' }}>
                {isAr ? 'تأكيد الخروج' : 'Confirm Checkout'}
              </Typography>
            )}
          </Box>
        </Box>

      </Box>
    </Dialog>
  );
};

export default ConfirmCheckoutModal;
